import { Spinner } from "../../../atoms/Spinner";
import {
  HiOutlineUsers,
  HiOutlineOfficeBuilding,
  HiLockClosed,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import useAuth from "../../../../hooks/useAuth";
import React, { useEffect, useState } from "react";
import LoginModal from "./LoginModal";
import EnterCallbackUrlModal from "./EnterCallbackUrlModal";
import IntegrationGuide from "./IntegrationGuide";
import { loginFormObject } from "./types";
import { Helmet } from "react-helmet-async";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import CopyToClipboardButton from "../../../atoms/CopyToClipboardButton";
import {
  useGetEntity,
  useGetLoginFormActive,
  useGetEntityPreferencesByPath,
  useGetProcess,
  useSetLoginFormObject,
  useUpdateLoginFormActive,
  useUpdateEntityPreferencesByPath,
  useUpdateShareState,
} from "../../../../hooks/entity";
import { usePermissions, PlanTier } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { PlansModal } from "../../../modals/PlansModal";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import { ResponsivePreviewLayout } from "../../../atoms/ResponsivePreviewLayout";

const TempPart = () => {
  const { t } = useTranslation();
  const { customer_identification, user, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const [showMore, setShowMore] = useState(false);
  const [showCallbackUrlModal, setCallbackUrlModal] = useState(false);
  const [callbackUrlLink, setCallbackUrlLink] = useState<string | null>(null);
  const [code, setCode] = useState<string>();
  const [serviceData, setServiceData] = useState<any>({
    name: "",
    logoUrl: "",
  });
  const [showReadyToUseUpgradeModal, setShowReadyToUseUpgradeModal] =
    useState(false);
  const [audience, setAudience] = useState<"customers" | "team">("customers");
  const isTeamAudience = audience === "team";
  const canManageTeam = permissions.canManageRoles();
  const teamMemberLoginLink = `${process.env.REACT_APP_HOST_URL}/login?entity_identification=${user.entity_identification}`;

  const {
    data: processData,
    isLoading,
    isError,
    refetch,
  } = useGetProcess(customer_identification, user.entity_identification);
  const isFreeUser = permissions.isPlanTier(PlanTier.FREE);

  const { mutate: updateShareData, isLoading: isUpdatingShareState } =
    useUpdateShareState();

  const loginToggleDisabled =
    isFreeUser ||
    isUpdatingShareState ||
    userEndedSubscription ||
    !permissions.can(Cap.APIS, Level.WRITE);

  const loginFormActiveData = useGetLoginFormActive(
    customer_identification,
    user.entity_identification,
    {
      onSuccess: (data) => {
        if (data?.is_login_form_active && !isFreeUser) {
          setShowMore(true);
          updateShareData({
            customer_identification: customer_identification,
            entity_identification: user.entity_identification,
          });
        } else {
          setShowMore(false);
        }
      },
    }
  );

  const { mutate: updateLoginFormActive, isLoading: isUpdatingOwnerData } =
    useUpdateLoginFormActive();

  const { data, isSuccess } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "cdn_logo_project_path,login_form_callback_link"
  );

  const { mutate: updatePreferences, isLoading: isUpdating } =
    useUpdateEntityPreferencesByPath();

  const { mutate: setLoginFormObject, isLoading: isSettingLoginForm } =
    useSetLoginFormObject();

  const { data: entity, isSuccess: entityIsSuccess } = useGetEntity(
    customer_identification,
    user.entity_identification,
    { enabled: isSuccess }
  );

  useEffect(() => {
    if (processData?.process_identification)
      setCode(`login/${processData.process_identification}`);
  }, [processData]);

  useEffect(() => {
    if (entityIsSuccess && isSuccess) {
      setServiceData({
        name: entity?.entity_name || "",
        logoUrl: data?.cdn_logo_project_path || "",
      });
      setCallbackUrlLink(data?.login_form_callback_link || null);
    }
  }, [isSuccess, entityIsSuccess, entity?.entity_name, data]);

  const updateEntityOwnerData = async (isActive: boolean) => {
    const value = {
      customer_identification: customer_identification,
      entity_identification: user.entity_identification,
      is_active: isActive,
    };
    updateLoginFormActive(value, {
      onSuccess: () => {
        updateShareData(
          {
            customer_identification: customer_identification,
            entity_identification: user.entity_identification,
          },
          {
            onSuccess: (data: any) => {
              setCode(`login/${data?.data?.element?.process_identification}`);
            },
          }
        );
      },
    });
  };

  const handleUpdateShareCondition = () => {
    if (!showMore) {
      setCallbackUrlModal(true);
      setShowMore(true);
      updateEntityOwnerData(true);
    } else {
      setShowMore(false);
      updateEntityOwnerData(false);
    }
  };

  const getCallBackUrlFromCustomer = (url: string) => {
    if (url) {
      setCallbackUrlLink(url);
      updatePreferences(
        {
          user_identification: customer_identification,
          entity_identification: user.entity_identification,
          path: "login_form_callback_link",
          value: url,
        },
        {
          onSuccess: () => {
            setShowMore(true);
            updateEntityOwnerData(true);
            setLoginFormObject({
              customer_identification: customer_identification,
              entity_identification: user.entity_identification,
              formObject: loginFormObject,
            });
          },
        }
      );
    }
  };

  if (loginFormActiveData.isLoading || isLoading) {
    return (
      <div className="w-full p-5 mx-auto flex justify-center items-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (loginFormActiveData.isError || isError) {
    return (
      <div className="w-full p-5 mx-auto flex justify-start items-center gap-3">
        <span>
          {t("dashboard.features.loginTemplate.tempPart.error.failedToLoad")}
        </span>
        <CustomButton
          onClick={() => {
            loginFormActiveData.refetch();
            refetch();
          }}
          title={t("dashboard.features.loginTemplate.tempPart.error.reload")}
        />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {t("dashboard.features.loginTemplate.tempPart.meta.title")}
        </title>
        <meta
          name="description"
          content={
            t(
              "dashboard.features.loginTemplate.tempPart.meta.description"
            ) as string
          }
        />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/control-panel/login-template`}
        />
      </Helmet>

      <ResponsivePreviewLayout
        configColSpan={4}
        previewColSpan={8}
        hasPreviewContent={true}
        previewContent={
          <div className="w-full h-full p-4 pt-6 xl:p-6 bg-gray-100 flex-col justify-start items-center gap-6 inline-flex">
            <h2 className="w-full text-lg font-semibold text-gray-900">
              {t("dashboard.features.loginTemplate.tempPart.btn-01")}
            </h2>

            {isTeamAudience ? (
              <img
                src="https://cdn.1cdn.app/application/UMD/26071120420634_login_member.png"
                alt={
                  t(
                    "dashboard.features.loginTemplate.tempPart.audience.team.previewAlt"
                  ) as string
                }
                className="w-full max-w-2xl rounded-lg border border-gray-200"
                loading="lazy"
              />
            ) : (
              <LoginModal
                showModal={showCallbackUrlModal || showMore}
                serviceData={serviceData}
                isRealLogin={false}
                showPreview={true}
              />
            )}
          </div>
        }
      >
        <div className="w-full p-4 xl:p-6 h-[calc(100vh-64px)] flex-col justify-start items-start gap-6 inline-flex bg-white overflow-y-auto">
          <div className="w-full min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              {t("dashboard.features.loginTemplate.tempPart.header")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("dashboard.features.loginTemplate.tempPart.pageSubtitle")}
            </p>
          </div>

          <div className="w-full flex flex-col gap-2">
            <span className="text-gray-700 text-sm font-medium">
              {t("dashboard.features.loginTemplate.tempPart.audience.label")}
            </span>
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(
                [
                  { key: "customers", icon: <HiOutlineUsers size={20} /> },
                  { key: "team", icon: <HiOutlineOfficeBuilding size={20} /> },
                ] as const
              ).map(({ key, icon }) => {
                const active = audience === key;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setAudience(key)}
                    className={`flex items-center gap-3 rounded-lg border p-4 text-left transition-colors ${
                      active
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        active
                          ? "bg-blue-100 text-blue-600"
                          : "bg-gray-50 text-gray-600"
                      }`}
                    >
                      {icon}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {t(
                        `dashboard.features.loginTemplate.tempPart.audience.${key}.title`
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          {isTeamAudience ? (
            !canManageTeam ? (
              <div className="w-full max-w-xl">
                <FeatureUpgradePrompt
                  icon={HiLockClosed}
                  iconTheme="blue"
                  title={t("roleEditor.lockedTitle") as string}
                  description={t("roleEditor.lockedDesc") as string}
                  benefitObjects={[
                    {
                      id: "roles-custom",
                      text: t("roleEditor.lockedBenefitRoles") as string,
                    },
                    {
                      id: "roles-scope",
                      text: t("roleEditor.lockedBenefitScope") as string,
                    },
                    {
                      id: "roles-delegate",
                      text: t("roleEditor.lockedBenefitDelegate") as string,
                    },
                  ]}
                  upgradeText={t("roleEditor.lockedUpgrade") as string}
                  onUpgrade={() => setShowReadyToUseUpgradeModal(true)}
                />
              </div>
            ) : (
              <div className="w-full max-w-xl flex flex-col gap-4">
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t(
                    "dashboard.features.loginTemplate.tempPart.audience.team.guide.intro"
                  )}
                </p>

                <div className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                    1
                  </span>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t(
                      "dashboard.features.loginTemplate.tempPart.audience.team.guide.step1"
                    )}
                  </p>
                </div>

                <div className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                    2
                  </span>
                  <div className="flex flex-col gap-2 w-full">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {t(
                        "dashboard.features.loginTemplate.tempPart.audience.team.guide.step2"
                      )}
                    </p>
                    <div className="w-full px-3 py-2 bg-gray-50 rounded-md border border-gray-200 flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <HiOutlineOfficeBuilding
                          size={18}
                          className="text-gray-400 shrink-0"
                        />
                        <span className="truncate text-gray-800 text-sm font-bold font-mono">
                          {user.entity_identification}
                        </span>
                      </div>
                      <Copy
                        target={user.entity_identification}
                        iconOnly
                        title={
                          t(
                            "dashboard.features.loginTemplate.tempPart.btn-03"
                          ) as string
                        }
                        className="cursor-pointer"
                        iconClassName="h-5 w-5 hover:text-blue-600"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                    3
                  </span>
                  <div className="flex flex-col gap-1">
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {t(
                        "dashboard.features.loginTemplate.tempPart.audience.team.guide.step3"
                      )}
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed">
                      {t(
                        "dashboard.features.loginTemplate.tempPart.audience.team.guide.passwordNote"
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-2 border-t border-gray-200">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t(
                      "dashboard.features.loginTemplate.tempPart.audience.team.guide.shareIntro"
                    )}
                  </p>
                  <div className="inline-flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white py-2 pl-3 pr-2">
                    <span className="truncate text-sm text-gray-700">
                      {teamMemberLoginLink}
                    </span>
                    <CopyToClipboardButton
                      textToCopy={teamMemberLoginLink}
                      variant="secondary"
                      size="xs"
                      fullWidth={false}
                      label={t(
                        "dashboard.features.loginTemplate.tempPart.btn-03"
                      )}
                    />
                  </div>
                </div>
              </div>
            )
          ) : (
            <>
              <div className="flex items-center gap-2">
                {isUpdatingShareState ||
                isUpdatingOwnerData ||
                isUpdating ||
                isSettingLoginForm ? (
                  <div className="h-6 w-11 flex justify-center items-center">
                    <Spinner size="sm" className="flex items-center" />
                  </div>
                ) : (
                  <label
                    className={`switch shrink-0 ${
                      loginToggleDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      onChange={handleUpdateShareCondition}
                      checked={showMore}
                      disabled={loginToggleDisabled}
                    />
                    <div className="slider"></div>
                  </label>
                )}
                <span className="text-gray-700 text-sm font-normal font-figtree leading-tight">
                  {t("dashboard.features.loginTemplate.tempPart.title-01")}
                </span>
                {isFreeUser && (
                  <UnlockBadge
                    onClick={() => setShowReadyToUseUpgradeModal(true)}
                  />
                )}
              </div>
              {showMore &&
                !isUpdatingShareState &&
                !isUpdatingOwnerData &&
                !isUpdating &&
                !isSettingLoginForm && (
                  <div className="inline-flex w-full max-w-xl items-center justify-between gap-2 rounded-md border border-gray-200 bg-white py-2 pl-3 pr-2">
                    <span className="truncate text-sm text-gray-700">{`${process.env.REACT_APP_HOST_URL}/${code}`}</span>

                    <CopyToClipboardButton
                      textToCopy={`${process.env.REACT_APP_HOST_URL}/${code}`}
                      variant="secondary"
                      size="xs"
                      fullWidth={false}
                      disabled={isFreeUser || userEndedSubscription}
                      label={t(
                        "dashboard.features.loginTemplate.tempPart.btn-03"
                      )}
                    />
                  </div>
                )}

              <div className="w-full max-w-xl">
                <EnterCallbackUrlModal
                  showModal={showCallbackUrlModal || showMore}
                  getCallBackUrlFromCustomer={getCallBackUrlFromCustomer}
                  callbackUrlLink={callbackUrlLink}
                  disabled={isFreeUser || userEndedSubscription}
                  isLoading={isUpdating}
                />
              </div>

              <div className="w-full max-w-xl">
                <IntegrationGuide callbackUrl={callbackUrlLink} />
              </div>
            </>
          )}
        </div>
      </ResponsivePreviewLayout>

      <PlansModal
        showPlansModal={showReadyToUseUpgradeModal}
        setShowPlansModal={setShowReadyToUseUpgradeModal}
        targetPlan={PlanTier.PREMIUM}
        hideFooter={false}
      />
    </>
  );
};

export default TempPart;
